import React from "react";
import {
  IconArrowBackUp,
  IconAward,
  IconBrandMastercard,
  IconBrandPaypal,
  IconBrandVisa,
  IconCash,
  IconCreditCard,
  IconGift,
  IconReceipt,
  IconWallet,
  type TablerIcon,
} from "@tabler/icons-react";

/** Card-brand string → brand glyph (Tabler has no Amex/Discover; fall back). */
const BRAND_ICONS: Record<string, TablerIcon> = {
  visa: IconBrandVisa,
  mastercard: IconBrandMastercard,
  paypal: IconBrandPaypal,
};

/** True when the tender is a card (drives the "no 'Card' word" label rule). */
export function isCardMethod(
  method: string | null | undefined,
  last4?: string | null,
  brand?: string | null,
): boolean {
  if (last4 || brand) return true;
  const m = (method || "").toLowerCase();
  if (m.includes("gift")) return false;
  return m.includes("card") || m.includes("stripe") || m.includes("terminal");
}

/**
 * Maps a raw payment method (e.g. "Stripe_Card", "Cash", "Loyalty") to a
 * differentiating tender icon. A known card `brand` picks a brand glyph.
 * Refund rows override to a returned-arrow icon.
 */
export function iconForMethod(
  method: string | null | undefined,
  isRefund = false,
  brand?: string | null,
): TablerIcon {
  if (isRefund) return IconArrowBackUp;
  const b = (brand || "").toLowerCase();
  if (BRAND_ICONS[b]) return BRAND_ICONS[b];
  const m = (method || "").toLowerCase();
  if (m.includes("loyalty")) return IconAward;
  if (m.includes("gift")) return IconGift;
  if (isCardMethod(method, undefined, brand)) return IconCreditCard;
  if (m.includes("cash")) return IconCash;
  if (m.includes("check")) return IconReceipt;
  return IconWallet;
}

/**
 * Display label for the Method column. Cards drop the generic word "Card" and
 * show `{Brand} •• {last4}` (e.g. "Visa •• 4242"); non-card tenders keep their
 * word (`formatted`). Falls back to `formatted` for a card lacking brand/last4.
 */
export function methodLabel(
  rawMethod: string | null | undefined,
  formatted: string,
  brand?: string | null,
  last4?: string | null,
): string {
  if (!isCardMethod(rawMethod, last4, brand)) return formatted;
  const b = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "";
  const tail = last4 ? `•• ${last4}` : "";
  return [b, tail].filter(Boolean).join(" ").trim() || formatted;
}

interface PaymentMethodIconProps {
  method: string | null | undefined;
  isRefund?: boolean;
  brand?: string | null;
  size?: number;
  stroke?: number;
  color?: string;
}

/** Small inline icon differentiating the tender type. */
export function PaymentMethodIcon({
  method,
  isRefund = false,
  brand,
  size = 15,
  stroke = 1.9,
  color = "#555",
}: PaymentMethodIconProps) {
  const Icon = iconForMethod(method, isRefund, brand);
  return <Icon size={size} stroke={stroke} color={color} />;
}
